/** Small perceptual colormaps as 256-stop RGB lookup tables. */

export type ColormapName = "viridis" | "plasma" | "jet";

// Anchor stops (RGB 0..1); we linearly interpolate to a 256-entry LUT.
const ANCHORS: Record<ColormapName, [number, number, number][]> = {
  viridis: [
    [0.267, 0.005, 0.329],
    [0.283, 0.141, 0.458],
    [0.254, 0.265, 0.53],
    [0.207, 0.372, 0.553],
    [0.164, 0.471, 0.558],
    [0.128, 0.567, 0.551],
    [0.135, 0.659, 0.518],
    [0.267, 0.749, 0.441],
    [0.478, 0.821, 0.318],
    [0.741, 0.873, 0.15],
    [0.993, 0.906, 0.144],
  ],
  plasma: [
    [0.05, 0.03, 0.53],
    [0.29, 0.01, 0.63],
    [0.49, 0.01, 0.66],
    [0.66, 0.13, 0.59],
    [0.8, 0.28, 0.47],
    [0.9, 0.42, 0.36],
    [0.97, 0.58, 0.25],
    [0.99, 0.75, 0.15],
    [0.94, 0.98, 0.13],
  ],
  jet: [
    [0.0, 0.0, 0.5],
    [0.0, 0.0, 1.0],
    [0.0, 1.0, 1.0],
    [0.5, 1.0, 0.5],
    [1.0, 1.0, 0.0],
    [1.0, 0.0, 0.0],
    [0.5, 0.0, 0.0],
  ],
};

export const COLORMAP_NAMES: ColormapName[] = ["viridis", "plasma", "jet"];

/** Build a 256×3 Float32 LUT for a named colormap. */
export function buildLUT(name: ColormapName): Float32Array {
  const anchors = ANCHORS[name] ?? ANCHORS.viridis;
  const lut = new Float32Array(256 * 3);
  const seg = anchors.length - 1;
  for (let i = 0; i < 256; i++) {
    const t = (i / 255) * seg;
    const lo = Math.min(Math.floor(t), seg - 1);
    const f = t - lo;
    const a = anchors[lo];
    const b = anchors[lo + 1];
    lut[i * 3] = a[0] + (b[0] - a[0]) * f;
    lut[i * 3 + 1] = a[1] + (b[1] - a[1]) * f;
    lut[i * 3 + 2] = a[2] + (b[2] - a[2]) * f;
  }
  return lut;
}

/** Sample a LUT at normalised t (0..1) → [r,g,b] in 0..1. */
export function sampleLUT(lut: Float32Array, t: number): [number, number, number] {
  const i = Math.max(0, Math.min(255, Math.round(t * 255)));
  return [lut[i * 3], lut[i * 3 + 1], lut[i * 3 + 2]];
}

/** CSS linear-gradient string for a colormap (scalar-bar rendering). */
export function toCssGradient(name: ColormapName): string {
  const lut = buildLUT(name);
  const stops: string[] = [];
  for (let i = 0; i <= 10; i++) {
    const [r, g, b] = sampleLUT(lut, i / 10);
    const rgb = `rgb(${Math.round(r * 255)},${Math.round(g * 255)},${Math.round(b * 255)})`;
    stops.push(`${rgb} ${i * 10}%`);
  }
  return `linear-gradient(to right, ${stops.join(", ")})`;
}
